// ProjectItemManagement.js
// Components to manage awards, press links, and fabricants with inline editing
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Input from '../Atoms/Input';

// Styled components
const ItemContainer = styled.div`
  margin-bottom: 15px;
  border: ${props => props.isEditing ? '1px solid #0000FF' : '1px solid #ddd'};
  padding: 15px;
  border-radius: 4px;
  background-color: ${props => props.isEditing ? '#f5f9ff' : 'white'};
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.isEditing ? '15px' : '0'};
`;

const ItemTitle = styled.div`
  font-weight: bold;
`;

const ItemContent = styled.div`
  margin-top: 10px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const ItemButton = styled.button`
  background-color: ${props => props.color || '#0000FF'};
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const FormRow = styled.div`
  margin-bottom: 10px;
  
  .label {
    font-weight: bold;
    color: rgb(0, 0, 255);
    margin-bottom: 5px;
    display: block;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Award Item Component
export const AwardItemComponent = ({ 
  item, 
  index, 
  isEditing, 
  isNew,
  onEdit, 
  onRemove, 
  onFinishEditing,
  onChange
}) => {
  const { i18n } = useTranslation('common');
  
  return (
    <ItemContainer isEditing={isEditing}>
      <ItemHeader isEditing={isEditing}>
        {!isEditing ? (
          <>
            <ItemTitle>
              {item.title} {item.year ? `(${item.year})` : ''}
            </ItemTitle>
            <ButtonGroup>
              <ItemButton onClick={() => onEdit(index)}>
                {i18n.language === 'fr' ? 'Modifier' : 'Edit'}
              </ItemButton>
              <ItemButton color="#f44336" onClick={() => onRemove(index)}>
                {i18n.language === 'fr' ? 'Supprimer' : 'Remove'}
              </ItemButton>
            </ButtonGroup>
          </>
        ) : (
          <ItemTitle>
            {isNew 
              ? (i18n.language === 'fr' ? 'Nouvelle récompense' : 'New Award') 
              : (i18n.language === 'fr' ? 'Modifier la récompense' : 'Edit Award')}
          </ItemTitle>
        )}
      </ItemHeader>
      
      {isEditing && (
        <ItemContent>
          <FormGrid>
            <FormRow>
              <div className="label">{i18n.language === 'fr' ? 'Titre:' : 'Title:'}</div>
              <Input 
                value={item.title || ''} 
                onChange={(e) => onChange(index, 'title', e.target.value)}
                style={{ width: '100%' }} 
                placeholder={i18n.language === 'fr' ? 'Nom de la récompense' : 'Award name'}
              />
            </FormRow>
            <FormRow>
              <div className="label">{i18n.language === 'fr' ? 'Année:' : 'Year:'}</div>
              <Input 
                value={item.year || ''} 
                onChange={(e) => onChange(index, 'year', e.target.value)}
                style={{ width: '100%' }} 
                placeholder={i18n.language === 'fr' ? 'Année d\'obtention' : 'Year received'}
              />
            </FormRow>
          </FormGrid>
          
          <ButtonGroup>
            <ItemButton 
              onClick={() => onFinishEditing(index)}
              disabled={!item.title}
            >
              {i18n.language === 'fr' ? 'Ajouter' : 'Add'}
            </ItemButton>
            <ItemButton 
              color="#f44336" 
              onClick={() => {
                if (isNew) {
                  onRemove(index);
                } else {
                  onEdit(null);
                }
              }}
            >
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </ItemButton>
          </ButtonGroup>
        </ItemContent>
      )}
    </ItemContainer>
  );
};

// Press Link Item Component
export const PressLinkItemComponent = ({ 
  item, 
  index, 
  isEditing, 
  isNew,
  onEdit, 
  onRemove, 
  onFinishEditing,
  onChange
}) => {
  const { i18n } = useTranslation('common');
  
  return (
    <ItemContainer isEditing={isEditing}>
      <ItemHeader isEditing={isEditing}>
        {!isEditing ? (
          <>
            <ItemTitle>
              {item.title}
            </ItemTitle>
            <ButtonGroup>
              <ItemButton onClick={() => onEdit(index)}>
                {i18n.language === 'fr' ? 'Modifier' : 'Edit'}
              </ItemButton>
              <ItemButton color="#f44336" onClick={() => onRemove(index)}>
                {i18n.language === 'fr' ? 'Supprimer' : 'Remove'}
              </ItemButton>
            </ButtonGroup>
          </>
        ) : (
          <ItemTitle>
            {isNew 
              ? (i18n.language === 'fr' ? 'Nouveau lien presse' : 'New Press Link') 
              : (i18n.language === 'fr' ? 'Modifier le lien presse' : 'Edit Press Link')}
          </ItemTitle>
        )}
      </ItemHeader>
      
      {!isEditing && item.url && (
        <div style={{ marginTop: '5px' }}>
          <a href={item.url.startsWith('http') ? item.url : `https://${item.url}`} target="_blank" rel="noopener noreferrer">
            {item.url}
          </a>
        </div>
      )}
      
      {isEditing && (
        <ItemContent>
          <FormGrid>
            <FormRow>
              <div className="label">{i18n.language === 'fr' ? 'Titre:' : 'Title:'}</div>
              <Input 
                value={item.title || ''} 
                onChange={(e) => onChange(index, 'title', e.target.value)}
                style={{ width: '100%' }} 
                placeholder={i18n.language === 'fr' ? 'Titre de l\'article' : 'Article title'}
              />
            </FormRow>
            <FormRow>
              <div className="label">{i18n.language === 'fr' ? 'URL:' : 'URL:'}</div>
              <Input 
                value={item.url || ''} 
                onChange={(e) => onChange(index, 'url', e.target.value)}
                style={{ width: '100%' }} 
                placeholder="https://example.com"
              />
            </FormRow>
          </FormGrid>
          
          <ButtonGroup>
          <ItemButton 
              onClick={() => onFinishEditing(index)}
              disabled={!item.title || !item.url}
            >
              {i18n.language === 'fr' ? 'Ajouter' : 'Add'}
            </ItemButton>
            <ItemButton 
              color="#f44336" 
              onClick={() => {
                if (isNew) {
                  onRemove(index);
                } else {
                  onEdit(null);
                }
              }}
            >
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </ItemButton>
          </ButtonGroup>
        </ItemContent>
      )}
    </ItemContainer>
  );
};

// Fabricant Item Component
export const FabricantItemComponent = ({ 
  item, 
  index, 
  isEditing, 
  isNew,
  onEdit, 
  onRemove, 
  onFinishEditing,
  onChange
}) => {
  const { i18n } = useTranslation('common');
  
  return (
    <ItemContainer isEditing={isEditing}>
      <ItemHeader isEditing={isEditing}>
        {!isEditing ? (
          <>
            <ItemTitle>
              {item.name} {item.work ? `- ${item.work}` : ''}
            </ItemTitle>
            <ButtonGroup>
              <ItemButton onClick={() => onEdit(index)}>
                {i18n.language === 'fr' ? 'Modifier' : 'Edit'}
              </ItemButton>
              <ItemButton color="#f44336" onClick={() => onRemove(index)}>
                {i18n.language === 'fr' ? 'Supprimer' : 'Remove'}
              </ItemButton>
            </ButtonGroup>
          </>
        ) : (
          <ItemTitle>
            {isNew 
              ? (i18n.language === 'fr' ? 'Nouveau fabricant' : 'New Manufacturer') 
              : (i18n.language === 'fr' ? 'Modifier le fabricant' : 'Edit Manufacturer')}
          </ItemTitle>
        )}
      </ItemHeader>
      
      {!isEditing && (
        <div style={{ marginTop: '5px' }}>
          {item.contact && <div><strong>{i18n.language === 'fr' ? 'Contact: ' : 'Contact: '}</strong>{item.contact}</div>}
          {item.email && <div><strong>Email: </strong>{item.email}</div>}
        </div>
      )}
      
      {isEditing && (
        <ItemContent>
          <FormRow>
            <div className="label">{i18n.language === 'fr' ? 'Nom du fabricant:' : 'Manufacturer name:'}</div>
            <Input 
              value={item.name || ''} 
              onChange={(e) => onChange(index, 'name', e.target.value)}
              style={{ width: '100%' }} 
              placeholder={i18n.language === 'fr' ? 'Nom de l\'entreprise' : 'Company name'}
            />
          </FormRow>
          
          <FormRow>
            <div className="label">{i18n.language === 'fr' ? 'Travail réalisé:' : 'Work done:'}</div>
            <Input 
              value={item.work || ''} 
              onChange={(e) => onChange(index, 'work', e.target.value)}
              style={{ width: '100%' }} 
              placeholder={i18n.language === 'fr' ? 'Description du travail réalisé' : 'Description of work done'}
            />
          </FormRow>
          
          <FormGrid>
            <FormRow>
              <div className="label">{i18n.language === 'fr' ? 'Contact:' : 'Contact:'}</div>
              <Input 
                value={item.contact || ''} 
                onChange={(e) => onChange(index, 'contact', e.target.value)}
                style={{ width: '100%' }} 
                placeholder={i18n.language === 'fr' ? 'Nom du contact' : 'Contact name'}
              />
            </FormRow>
            
            <FormRow>
              <div className="label">{i18n.language === 'fr' ? 'Email:' : 'Email:'}</div>
              <Input 
                value={item.email || ''} 
                onChange={(e) => onChange(index, 'email', e.target.value)}
                style={{ width: '100%' }} 
                placeholder="email@example.com"
              />
            </FormRow>
          </FormGrid>
          
          <ButtonGroup>
            <ItemButton 
              onClick={() => onFinishEditing(index)}
              disabled={!item.name}
            >
              {i18n.language === 'fr' ? 'Ajouter' : 'Add'}
            </ItemButton>
            <ItemButton 
              color="#f44336" 
              onClick={() => {
                if (isNew) {
                  onRemove(index);
                } else {
                  onEdit(null);
                }
              }}
            >
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </ItemButton>
          </ButtonGroup>
        </ItemContent>
      )}
    </ItemContainer>
  );
};

export default {
  AwardItemComponent,
  PressLinkItemComponent,
  FabricantItemComponent
};